<script lang="ts" module>
	export interface PdpGalleryProps {
		items: GalleryItem[];
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { swipe, type SwipeCustomEvent } from 'svelte-gestures';
	import { twMerge } from 'tailwind-merge';
	import { PlayIcon } from '../../../../assets/icons/PlayIcon';
	import type { Lightbox as LightboxComponent } from '../../../../components/Lightbox';
	import { GalleryMedia, type GalleryItem } from '../../../../components/Lightbox/types';
	import { loadLightbox } from '../../../../components/Lightbox/utils/loadLightbox/loadLightbox';
	import { Txt } from '../../../../components/Txt';
	import { VideoPlayer } from '../../../../components/VideoPlayer';
	import { WhiteBorderImageContainer } from '../../../../components/WhiteBorderImageContainer';
	import { getVideoImageUrl } from './utils/getVideoImageUrl';

	let {
		legend = undefined,
		dataCy,
		items,
		selectedItemIndex = $bindable(),
		isLightboxOpen = $bindable(),
		includeLightbox = true,
		classes = {},
		...rest
	}: Props = $props();

	interface Props {
		class?: string;
		legend?: string | undefined | null;
		dataCy: string;
		items: GalleryItem[];
		selectedItemIndex: number;
		isLightboxOpen: boolean;
		includeLightbox?: boolean;
		classes?: {
			mainImage?: string;
		};
	}

	let dataCyPrefix = $derived(`${dataCy}-gallery`);

	$effect(() => {
		if (items) {
			selectedItemIndex = 0;
		}
	});

	let selectedItem = $derived(items[selectedItemIndex]);

	const handleSelectItem = (_: GalleryItem, index: number) => {
		selectedItemIndex = index;
	};

	// The lightbox starts closed, so it loads on demand: prefetched when the
	// main image is pointed at or focused, and rendered once it has arrived.
	let Lightbox = $state<typeof LightboxComponent>();

	const prefetchLightbox = () => {
		if (Lightbox) return;

		loadLightbox()
			.then((component) => {
				Lightbox = component;
			})
			.catch(() => {
				// Retried on the next open.
			});
	};

	$effect(() => {
		// Also opened from outside through the bound `isLightboxOpen`.
		if (isLightboxOpen) prefetchLightbox();
	});

	const handleOpenLightbox = () => {
		if (!includeLightbox) return;

		prefetchLightbox();
		isLightboxOpen = true;
	};

	const handleCloseLightbox = () => {
		isLightboxOpen = false;
	};

	const handleSelectNextItem = () => {
		selectedItemIndex = (selectedItemIndex + 1) % items.length;
	};

	const handleSelectPreviousItem = () => {
		selectedItemIndex = +(selectedItemIndex - 1 + items.length) % items.length;
	};

	const handleDotClick = (index: number) => {
		selectedItemIndex = index;
	};

	const handleSwipe = (event: SwipeCustomEvent) => {
		if (event.detail.direction === 'left') {
			handleSelectNextItem();
		} else if (event.detail.direction === 'right') {
			handleSelectPreviousItem();
		}
	};
</script>

<div data-cy={dataCyPrefix} class={rest.class}>
	<div
		class="!touch-pan-y"
		use:swipe={() => ({ timeframe: 300, minSwipeDistance: 60 })}
		onswipe={handleSwipe}
	>
		{#if selectedItem}
			{#if selectedItem.type === GalleryMedia.video}
				<div
					data-cy={`${dataCyPrefix}-main-video`}
					class="bg-gray-75 relative mb-2 w-full pb-[min(500px,100%)]"
				>
					<div
						class={classNames(
							'absolute flex w-full items-center justify-center p-6',
							selectedItem.media.width < selectedItem.media.height
								? 'h-[min(500px,100%)]'
								: 'h-full'
						)}
					>
						<VideoPlayer
							class={classNames('relative w-full', {
								'h-[min(500px,100%)]': selectedItem.media.width < selectedItem.media.height
							})}
							src={selectedItem.media.url}
							title={selectedItem.title}
							autoplay={false}
							loop={false}
							controls={true}
							playsinline={false}
							dataCy={selectedItem.title}
							style={`aspect-ratio: ${selectedItem.media.width} / ${selectedItem.media.height}`}
						/>
					</div>
				</div>
			{:else}
				<button
					data-cy={`${dataCyPrefix}-main-image-button`}
					onclick={handleOpenLightbox}
					onpointerenter={includeLightbox ? prefetchLightbox : undefined}
					onfocus={includeLightbox ? prefetchLightbox : undefined}
					class={classNames(
						'bg-gray-75 relative mb-2 block w-full pb-[min(500px,100%)] outline-none',
						{
							'cursor-default': !includeLightbox
						}
					)}
				>
					<div
						class={classNames('absolute left-0 top-0 h-full w-full', {
							'cursor-default': !includeLightbox
						})}
					>
						<WhiteBorderImageContainer
							class={twMerge('h-full p-6', classes.mainImage)}
							image={selectedItem.media}
							loading="eager"
							alt={`${selectedItem.title} main image`}
							dataCy={`${dataCyPrefix}-main-image`}
						/>
					</div>

					{#if legend}
						<Txt variant="label4" class="absolute bottom-3 left-3 text-gray-500">
							{legend}
						</Txt>
					{/if}
				</button>
			{/if}
		{/if}
	</div>

	{#if items.length > 1}
		<div class="flex flex-wrap gap-2">
			{#each items as item, index}
				<button
					data-cy={`${dataCyPrefix}-image-button`}
					class={classNames('border-2 transition-colors', {
						'border-gray-300': index === selectedItemIndex,
						'border-transparent': index !== selectedItemIndex
					})}
					onclick={() => handleSelectItem(item, index)}
				>
					<div
						class="bg-gray-75 relative h-[3.5rem] w-[3.5rem] items-center justify-center md:h-[5rem] md:w-[5rem]"
					>
						{#if item.type === GalleryMedia.image}
							<div class="absolute left-0 top-0 h-full w-full">
								<WhiteBorderImageContainer
									class="h-[3.5rem] p-1 md:h-[5rem]"
									image={item.media}
									alt={item.title}
									dataCy={dataCyPrefix}
								/>
							</div>
						{:else}
							<PlayIcon
								color="gray-100"
								class="absolute left-1/2 top-1/2 z-10 m-auto -translate-x-1/2 -translate-y-1/2"
							/>
							<img
								data-cy={`${dataCyPrefix}-gallery-image`}
								class="absolute left-1/2 top-1/2 m-auto h-auto max-h-full w-auto max-w-full -translate-x-1/2 -translate-y-1/2 object-contain p-1"
								alt={item.title}
								src={getVideoImageUrl(item.media.url, items[0].media.url)}
							/>
						{/if}
					</div>
				</button>
			{/each}
		</div>
	{/if}
</div>

{#if isLightboxOpen && Lightbox}
	<Lightbox
		dataCy={dataCyPrefix}
		onClose={handleCloseLightbox}
		{selectedItem}
		{selectedItemIndex}
		itemsLength={items.length}
		onClickLeftArrow={handleSelectPreviousItem}
		onClickRightArrow={handleSelectNextItem}
		onDotClick={handleDotClick}
	/>
{/if}
